import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Skeleton } from '../components/ui/skeleton';
import Confetti from 'react-confetti';
import {
  CheckCircle,
  XCircle,
  Loader2,
  Crown,
  ShoppingBag,
  Home,
  Settings,
  RefreshCw,
  Gift,
  Sparkles,
  ArrowRight
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

export default function PaymentSuccess() {
  const { token, refreshUser } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  
  const [status, setStatus] = useState('loading'); // loading, success, failed, error
  const [paymentData, setPaymentData] = useState(null);
  const [showConfetti, setShowConfetti] = useState(false);
  const [windowSize, setWindowSize] = useState({ width: window.innerWidth, height: window.innerHeight });

  const sessionId = searchParams.get('session_id');
  const paymentType = searchParams.get('type') || 'membership';

  // Handle window resize for confetti
  useEffect(() => {
    const handleResize = () => {
      setWindowSize({ width: window.innerWidth, height: window.innerHeight });
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Check payment status
  useEffect(() => {
    const checkPayment = async () => {
      if (!sessionId) {
        setStatus('error');
        return;
      }

      try {
        const response = await fetch(`${API}/api/payments/status/${sessionId}`, {
          headers: token ? { Authorization: `Bearer ${token}` } : {}
        });

        if (response.ok) {
          const data = await response.json();
          setPaymentData(data);
          
          if (data.payment_status === 'paid' || data.status === 'complete') {
            setStatus('success');
            setShowConfetti(true);
            // Refresh user data to get updated membership
            if (refreshUser) {
              await refreshUser();
            }
            // Stop confetti after 5 seconds
            setTimeout(() => setShowConfetti(false), 5000);
          } else if (data.payment_status === 'unpaid' || data.status === 'expired') {
            setStatus('failed');
          } else {
            // Still processing, check again in 2 seconds
            setTimeout(checkPayment, 2000);
          }
        } else {
          setStatus('error');
        }
      } catch (error) {
        console.error('Payment status check failed:', error);
        setStatus('error');
      }
    };

    checkPayment();
  }, [sessionId, token, refreshUser]);

  // Loading state
  if (status === 'loading') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <Card className="w-full max-w-md mx-4">
          <CardContent className="pt-8 pb-8 text-center">
            <Loader2 className="w-16 h-16 mx-auto mb-4 animate-spin text-primary" />
            <h2 className="text-xl font-semibold mb-2">Processing Payment</h2>
            <p className="text-muted-foreground">Please wait while we confirm your payment...</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Success state
  if (status === 'success') {
    const isMembership = paymentType === 'membership' || paymentData?.metadata?.payment_type === 'membership';
    
    return (
      <div className="min-h-screen flex items-center justify-center bg-background p-4">
        {showConfetti && (
          <Confetti
            width={windowSize.width}
            height={windowSize.height}
            recycle={false}
            numberOfPieces={300}
            gravity={0.2}
          />
        )}
        
        <Card className="w-full max-w-lg relative overflow-hidden">
          {/* Decorative gradient */}
          <div className="absolute top-0 left-0 right-0 h-2 bg-gradient-to-r from-green-400 via-emerald-500 to-teal-500" />
          
          <CardHeader className="text-center pt-8">
            <div className="w-20 h-20 mx-auto mb-4 rounded-full bg-green-100 dark:bg-green-900/30 flex items-center justify-center">
              <CheckCircle className="w-12 h-12 text-green-500" />
            </div>
            <CardTitle className="text-2xl">Payment Successful!</CardTitle>
            <CardDescription>
              Thank you for your purchase
            </CardDescription>
          </CardHeader>
          
          <CardContent className="space-y-6">
            {/* Order details */}
            <div className="bg-muted/50 rounded-lg p-4 space-y-3">
              <div className="flex items-center justify-between">
                <span className="text-muted-foreground">Type</span>
                <Badge variant="secondary" className="flex items-center gap-1">
                  {isMembership ? (
                    <>
                      <Crown className="w-3 h-3" />
                      Membership
                    </>
                  ) : (
                    <>
                      <ShoppingBag className="w-3 h-3" />
                      Purchase
                    </>
                  )}
                </Badge>
              </div>
              
              {paymentData?.metadata?.package_name && (
                <div className="flex items-center justify-between">
                  <span className="text-muted-foreground">Plan</span>
                  <span className="font-medium">{paymentData.metadata.package_name}</span>
                </div>
              )}
              
              {paymentData?.amount && (
                <div className="flex items-center justify-between">
                  <span className="text-muted-foreground">Amount</span>
                  <span className="font-semibold text-lg">
                    ${paymentData.amount.toFixed(2)} {paymentData.currency?.toUpperCase()}
                  </span>
                </div>
              )}
              
              {paymentData?.metadata?.discount_applied && paymentData.metadata.discount_applied !== '0' && (
                <div className="flex items-center justify-between text-green-600">
                  <span className="flex items-center gap-1">
                    <Gift className="w-4 h-4" />
                    Referral Discount
                  </span>
                  <span>-{paymentData.metadata.discount_applied}%</span>
                </div>
              )}
            </div>

            {/* Membership benefits */}
            {isMembership && (
              <div className="space-y-3">
                <h4 className="font-medium flex items-center gap-2">
                  <Sparkles className="w-4 h-4 text-primary" />
                  Your new benefits
                </h4>
                <ul className="space-y-2 text-sm">
                  <li className="flex items-center gap-2">
                    <CheckCircle className="w-4 h-4 text-green-500" />
                    Ad-free browsing experience
                  </li>
                  <li className="flex items-center gap-2">
                    <CheckCircle className="w-4 h-4 text-green-500" />
                    Priority support access
                  </li>
                  <li className="flex items-center gap-2">
                    <CheckCircle className="w-4 h-4 text-green-500" />
                    Extended pet profiles
                  </li>
                  {paymentData?.metadata?.package_id?.includes('premium') && (
                    <>
                      <li className="flex items-center gap-2">
                        <CheckCircle className="w-4 h-4 text-green-500" />
                        Professional badge
                      </li>
                      <li className="flex items-center gap-2">
                        <CheckCircle className="w-4 h-4 text-green-500" />
                        Access to private forums
                      </li>
                    </>
                  )}
                </ul>
              </div>
            )}

            {/* Security notice */}
            <p className="text-[11px] text-muted-foreground text-center pt-2 border-t">
              Your card data was handled entirely by Stripe and never touched our servers.
            </p>

            {/* Action buttons */}
            <div className="flex flex-col sm:flex-row gap-3 pt-4">
              <Button 
                className="flex-1" 
                onClick={() => navigate('/feed')}
                data-testid="go-to-feed"
              >
                <Home className="w-4 h-4 mr-2" />
                Go to Feed
              </Button>
              <Button 
                variant="outline" 
                className="flex-1"
                onClick={() => navigate('/settings')}
                data-testid="go-to-settings"
              >
                <Settings className="w-4 h-4 mr-2" />
                View Settings
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Failed state
  if (status === 'failed') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background p-4">
        <Card className="w-full max-w-md">
          <CardHeader className="text-center pt-8">
            <div className="w-20 h-20 mx-auto mb-4 rounded-full bg-orange-100 dark:bg-orange-900/30 flex items-center justify-center">
              <XCircle className="w-12 h-12 text-orange-500" />
            </div>
            <CardTitle className="text-2xl">Payment Incomplete</CardTitle>
            <CardDescription>
              Your payment was not completed
            </CardDescription>
          </CardHeader>
          
          <CardContent className="space-y-6 text-center">
            <p className="text-muted-foreground">
              It looks like the payment was cancelled or expired. 
              No charges were made to your account.
            </p>
            
            <div className="flex flex-col sm:flex-row gap-3">
              <Button 
                className="flex-1" 
                onClick={() => navigate('/settings')}
              >
                <RefreshCw className="w-4 h-4 mr-2" />
                Try Again
              </Button>
              <Button 
                variant="outline" 
                className="flex-1"
                onClick={() => navigate('/')}
              >
                <Home className="w-4 h-4 mr-2" />
                Go Home
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Error state
  return (
    <div className="min-h-screen flex items-center justify-center bg-background p-4">
      <Card className="w-full max-w-md">
        <CardHeader className="text-center pt-8">
          <div className="w-20 h-20 mx-auto mb-4 rounded-full bg-red-100 dark:bg-red-900/30 flex items-center justify-center">
            <XCircle className="w-12 h-12 text-red-500" />
          </div>
          <CardTitle className="text-2xl">Something Went Wrong</CardTitle>
          <CardDescription>
            We couldn't verify your payment status
          </CardDescription>
        </CardHeader>
        
        <CardContent className="space-y-6 text-center">
          <p className="text-muted-foreground">
            Please check your email for a payment confirmation, or contact support if you need help.
          </p>
          
          <div className="flex flex-col sm:flex-row gap-3">
            <Button 
              variant="outline" 
              className="flex-1"
              onClick={() => window.location.reload()}
            >
              <RefreshCw className="w-4 h-4 mr-2" />
              Retry
            </Button>
            <Button 
              className="flex-1"
              onClick={() => navigate('/')}
            >
              <Home className="w-4 h-4 mr-2" />
              Go Home
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
